import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { addTimesheetWorkingHours, deleteTimesheetWorkingHours, getTimesheetCreationRecords, updateTimesheetHours } from '../../../../features/timesheet/timesheet.slice';
import TimesheetFilterHeader from '../../../../../components/TimesheetFilterHeader';
import TimesheetHeader from './TimesheetHeader';
import InvoiceByTimesheetModal, { InvoiceByTimesheetData } from '../../../../../components/InvoiceByTimesheetModal';
import { Tooltip } from '../../../../components/common/tooltip.component';

interface TimesheetDataEntry {
    id: number;
    timeSheetWorkingHourId?: number;
    workingDate: string;
    dateId: string;
    number: number;
    unit: string;
    vat: number;
    isArticleShownInInvoice: boolean;
    amountPerUnit: number;
    amount: number;
    articleNumber: string;
    workingDescription: string;
    houseworkType: string;
    workingHour: number;
    CustomWorkingDescription: string;
    userId: number;
    customerId?: number;
    customerName?: string;
}

interface TimesheetEntry {
    id: string;
    date: string;
    customerName?: string;
    taskDetails: string;
    pricingType: string;
    price: string;
    hours: string;
    holiday: string;
    articleNumber: string;
}

interface WeekGroup {
    weekNumber: number;
    startDate: string;
    endDate: string;
    customerName?: string;
    entries: TimesheetEntry[];
}

const TimesheetCreation: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch<any>();
    const { loading: savingTimesheet, timesheetData: fetchedTimesheetData, fetchLoading, numberOfPages, totalCount } = useSelector((state: any) => state.timesheet || { loading: false, timesheetData: [], fetchLoading: false, numberOfPages: 0, totalCount: 0 });
    
    // Get customers from Redux to map customer names
    const { customers } = useSelector((state: any) => state.customer || { customers: [] });
    
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [selectedClient, setSelectedClient] = useState<string>('');
    const [selectedDate, setSelectedDate] = useState<string>('');
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isSaveConfirmationOpen, setIsSaveConfirmationOpen] = useState<boolean>(false);
    const [isEditConfirmationOpen, setIsEditConfirmationOpen] = useState<boolean>(false);
    const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState<boolean>(false);
    const [entryToDelete, setEntryToDelete] = useState<{ weekIndex: number; entryId: string } | null>(null);
    const [editingRowId, setEditingRowId] = useState<string | null>(null);
    const [originalEditData, setOriginalEditData] = useState<{ timesheetEntry: TimesheetDataEntry | null; weekEntry: TimesheetEntry | null }>({ timesheetEntry: null, weekEntry: null });
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
    const [timesheetData, setTimesheetData] = useState<TimesheetDataEntry[]>([]);
    const isUserEditingRef = useRef<boolean>(false);

    // Sample data - Replace with actual API data
    const [weekGroups, setWeekGroups] = useState<WeekGroup[]>([
        {
            weekNumber: 24,
            startDate: '',
            endDate: '',
            entries: [
                {
                    id: '1',
                    date: '',
                    taskDetails: '',
                    pricingType: 'Hourly',
                    price: 'SEK',
                    hours: '00:00',
                    holiday: 'No',
                    articleNumber: '92'
                },
               
            ]
        }
    ]);

    const handleEntryChange = (weekIndex: number, entryId: string, field: keyof TimesheetEntry, value: string) => {
        let validatedValue = value;
        let updatedArticleNumber = '';

        // Validate price field - only numbers and one decimal point
        if (field === 'price') {
            // Remove 'SEK' suffix if present and trim
            const numericValue = value.replace(/[^0-9.]/g, '');
            // Ensure only one decimal point
            const parts = numericValue.split('.');
            if (parts.length > 2) {
                validatedValue = parts[0] + '.' + parts.slice(1).join('');
            } else {
                validatedValue = numericValue;
            }
            // Add 'SEK' suffix back
            validatedValue = validatedValue ? `${validatedValue} SEK` : 'SEK';
        }

        // Validate hours field - allow free editing, only basic character filtering
        if (field === 'hours') {
            // Allow only numbers and colon, max 5 characters (HH:MM)
            validatedValue = value.replace(/[^0-9:]/g, '').slice(0, 5);
            
            // Remove multiple colons, keep only first one
            const colonCount = (validatedValue.match(/:/g) || []).length;
            if (colonCount > 1) {
                const firstColonIndex = validatedValue.indexOf(':');
                validatedValue = validatedValue.slice(0, firstColonIndex + 1) + 
                               validatedValue.slice(firstColonIndex + 1).replace(/:/g, '');
            }
        }

        // Update weekGroups state
        setWeekGroups(prev => {
            const newGroups = [...prev];
            const entryIndex = newGroups[weekIndex].entries.findIndex(e => e.id === entryId);
            if (entryIndex !== -1) {
                // If pricing type is changed, update articleNumber and hours
                if (field === 'pricingType') {
                    const pricingDetails = getPricingTypeDetails(validatedValue);
                    updatedArticleNumber = pricingDetails.articleNumber;
                    
                    // For non-hourly pricing types, set hours to 01:00 by default
                    // For hourly, preserve existing hours
                    const newHours = updatedArticleNumber === '92' 
                        ? newGroups[weekIndex].entries[entryIndex].hours 
                        : '01:00';
                    
                    newGroups[weekIndex].entries[entryIndex] = {
                        ...newGroups[weekIndex].entries[entryIndex],
                        [field]: validatedValue,
                        articleNumber: updatedArticleNumber,
                        hours: newHours
                    };
                } else {
                    // For other fields, just update the field
                    newGroups[weekIndex].entries[entryIndex] = {
                        ...newGroups[weekIndex].entries[entryIndex],
                        [field]: validatedValue
                    };
                }
            }
            return newGroups;
        });

        // If currently editing a row, check if actual changes exist by comparing with original
        if (editingRowId && editingRowId === entryId) {
            // Compare after state updates complete
            setTimeout(() => {
                // Find current entry in weekGroups
                let currentEntry: TimesheetEntry | null = null;
                for (const group of weekGroups) {
                    const entry = group.entries.find(e => e.id === entryId);
                    if (entry) {
                        currentEntry = entry;
                        break;
                    }
                }
                
                // Compare with original to determine if there are actual changes
                if (currentEntry && originalEditData.weekEntry) {
                    const hasChanges = 
                        currentEntry.date !== originalEditData.weekEntry.date ||
                        currentEntry.taskDetails !== originalEditData.weekEntry.taskDetails ||
                        currentEntry.pricingType !== originalEditData.weekEntry.pricingType ||
                        currentEntry.price !== originalEditData.weekEntry.price ||
                        currentEntry.hours !== originalEditData.weekEntry.hours;
                    
                    setHasUnsavedChanges(hasChanges);
                }
            }, 0);
        }

        // Update timesheetData state with the changes
        isUserEditingRef.current = true;
        setTimesheetData(prevData => {
            // Find the matching entry in timesheetData using the id
            const dataIndex = prevData.findIndex(data => `${data.id}-${data.dateId}` === entryId);
            
            if (dataIndex === -1) {
                return prevData;
            }

            const currentData = prevData[dataIndex];
            let updatedEntry = { ...currentData };
            
            // Map the UI field changes back to timesheetData structure
            switch (field) {
                case 'date':
                    // Convert date format from YYYY-MM-DD to YYYY/MM/DD
                    updatedEntry = {
                        ...currentData,
                        workingDate: validatedValue.replace(/-/g, '/'),
                        dateId: validatedValue.replace(/-/g, '')
                    };
                    break;
                case 'taskDetails':
                    updatedEntry = {
                        ...currentData,
                        CustomWorkingDescription: validatedValue,
                        workingDescription: validatedValue
                    };
                    break;
                case 'pricingType':
                    const pricingDetails = getPricingTypeDetails(validatedValue);
                    const articleNum = pricingDetails.articleNumber;
                    const newUnit = pricingDetails.unit;
                    // For non-hourly types, set workingHour and number to 1
                    // For hourly, preserve existing workingHour
                    const newWorkingHour = articleNum === '92' ? currentData.workingHour : 1;
                    const newNumber = articleNum === '92' ? currentData.workingHour : 1;
                    
                    updatedEntry = {
                        ...currentData,
                        articleNumber: articleNum,
                        unit: newUnit,
                        workingHour: newWorkingHour,
                        number: newNumber,
                        amount: newNumber * currentData.amountPerUnit
                    };
                    break;
                case 'price':
                    // Extract numeric value from price
                    const priceValue = parseFloat(validatedValue.replace(/[^0-9.]/g, '')) || 0;
                    updatedEntry = {
                        ...currentData,
                        amountPerUnit: priceValue,
                        amount: currentData.number * priceValue
                    };
                    break;
                case 'hours':
                    // Convert HH:MM to decimal hours
                    const [hrs, mins] = validatedValue.split(':').map(v => parseInt(v) || 0);
                    const decimalHours = hrs + (mins / 60);
                    const newNum = currentData.articleNumber === '92' ? decimalHours : 1;
                    
                    updatedEntry = {
                        ...currentData,
                        workingHour: decimalHours,
                        number: newNum,
                        amount: newNum * currentData.amountPerUnit
                    };
                    break;
                case 'holiday':
                    // Store holiday status if needed in future
                    updatedEntry = {
                        ...currentData,
                        houseworkType: validatedValue
                    };
                    break;
            }

            // Create new array with updated entry
            const updatedData = [
                ...prevData.slice(0, dataIndex),
                updatedEntry,
                ...prevData.slice(dataIndex + 1)
            ];
            
            return updatedData;
        });
    };

    const handleDeleteEntry = (weekIndex: number, entryId: string) => {
        // Find the entry in timesheetData to check if it's a new entry (id === 0)
        const entryData = timesheetData.find(data => `${data.id}-${data.dateId}` === entryId);
        
        
        // If entry not found in timesheetData OR has id === 0 (new entry not saved to backend), 
        // remove directly without confirmation
        if (!entryData || entryData.id === 0) {
            
            // Remove from UI directly
            setWeekGroups(prev => {
                const newGroups = [...prev];
                newGroups[weekIndex] = {
                    ...newGroups[weekIndex],
                    entries: newGroups[weekIndex].entries.filter(e => e.id !== entryId)
                };
                return newGroups;
            });
            
            // Remove from timesheetData if exists
            setTimesheetData(prevData => {
                return prevData.filter(data => `${data.id}-${data.dateId}` !== entryId);
            });
            
            return;
        }
        
        
        // For existing entries (id !== 0), show confirmation modal and proceed with API call
        setEntryToDelete({ weekIndex, entryId });
        setIsDeleteConfirmationOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!entryToDelete) return;

        const { weekIndex, entryId } = entryToDelete;
        
        // Find the timeSheetWorkingHourId from timesheetData
        const entryData = timesheetData.find(data => `${data.id}-${data.dateId}` === entryId);
        
        if (!entryData) {
            // Remove from UI only if not found in timesheetData
            setWeekGroups(prev => {
                const newGroups = [...prev];
                newGroups[weekIndex].entries = newGroups[weekIndex].entries.filter(e => e.id !== entryId);
                return newGroups;
            });
            setIsDeleteConfirmationOpen(false);
            setEntryToDelete(null);
            return;
        }

        const timeSheetWorkingHourId = entryData.timeSheetWorkingHourId || entryData.id;
        
        try {
            // Call delete API with array of IDs
            await dispatch(
                deleteTimesheetWorkingHours({
                    timeSheetWorkingHourIds: [timeSheetWorkingHourId]
                })
            ).unwrap();
            
            // Remove from UI after successful deletion
            setWeekGroups(prev => {
                const newGroups = [...prev];
                newGroups[weekIndex].entries = newGroups[weekIndex].entries.filter(e => e.id !== entryId);
                return newGroups;
            });

            // Also remove from timesheetData
            setTimesheetData(prevData => {
                const updatedData = prevData.filter(data => `${data.id}-${data.dateId}` !== entryId);
                return updatedData;
            });
            
            // Close modal and reset state
            setIsDeleteConfirmationOpen(false);
            setEntryToDelete(null);
        } catch (error) {
            // Modal stays open on error so user can retry
        }
    };

    const handleCancelDelete = () => {
        setIsDeleteConfirmationOpen(false);
        setEntryToDelete(null);
    };

    const handleHoursBlur = (weekIndex: number, entryId: string, value: string) => {
        // Format hours on blur with max limit of 23:59
        if (!value || value === '') return;
        
        let formattedValue = value;
        
        if (!value.includes(':')) {
            // Only hours, no colon - add :00
            const hours = parseInt(value) || 0;
            const validHours = Math.min(Math.max(hours, 0), 23); // Ensure 0-23 range
            formattedValue = `${String(validHours).padStart(2, '0')}:00`;
        } else {
            // Has colon, format both parts with max 23:59
            const [hoursStr, minsStr] = value.split(':');
            const hours = parseInt(hoursStr) || 0;
            const mins = parseInt(minsStr) || 0;
            
            // Cap at maximum 23:59
            let validHours = Math.min(Math.max(hours, 0), 23); // Ensure 0-23 range
            let validMins = Math.min(Math.max(mins, 0), 59); // Ensure 0-59 range
            
            // If someone enters 24:00 or higher, cap to 23:59
            if (hours >= 24) {
                validHours = 23;
                validMins = 59;
            }
            
            formattedValue = `${String(validHours).padStart(2, '0')}:${String(validMins).padStart(2, '0')}`;
        }
        
        // Update with formatted value
        if (formattedValue !== value) {
            handleEntryChange(weekIndex, entryId, 'hours', formattedValue);
        }
    };

    const handleAddRecord = (timesheetDataArray?: TimesheetDataEntry[]) => {
        if (timesheetDataArray && timesheetDataArray.length > 0) {
            setTimesheetData(timesheetDataArray);
        }
    };

    // Fetch timesheet data on component mount with empty fromDate, toDate, and customerId
    useEffect(() => {
        dispatch(
            getTimesheetCreationRecords({
                fromDate: '',
                toDate: '',
                customerId: '',
                pageNumber: 1
            })
        );
    }, [dispatch]);

    // Convert timesheetData to weekGroups format when timesheetData changes
    useEffect(() => {
        // Skip if user is actively editing to prevent overwriting their input
        if (isUserEditingRef.current) {
            isUserEditingRef.current = false;
            return;
        }
        
        if (timesheetData.length > 0) {
            // Group data by week
            const groupedByWeek: { [key: string]: TimesheetDataEntry[] } = {};
            
            timesheetData.forEach(entry => {
                const date = new Date(entry.workingDate);
                const weekNumber = getWeekNumber(date);
                const weekKey = `${date.getFullYear()}-W${weekNumber}`;
                
                if (!groupedByWeek[weekKey]) {
                    groupedByWeek[weekKey] = [];
                }
                groupedByWeek[weekKey].push(entry);
            });

            // Convert to WeekGroup format
            const newWeekGroups: WeekGroup[] = Object.entries(groupedByWeek).map(([weekKey, entries]) => {
                const sortedEntries = entries.sort((a, b) => 
                    new Date(a.workingDate).getTime() - new Date(b.workingDate).getTime()
                );
                
                const firstDate = new Date(sortedEntries[0].workingDate);
                const lastDate = new Date(sortedEntries[sortedEntries.length - 1].workingDate);
                
                return {
                    weekNumber: getWeekNumber(firstDate),
                    startDate: formatDate(firstDate),
                    endDate: formatDate(lastDate),
                    customerName: sortedEntries[0]?.customerName || '',
                    entries: sortedEntries.map(entry => ({
                        id: `${entry.id}-${entry.dateId}`,
                        date: formatDateForInput(entry.workingDate),
                        customerName: entry.customerName || '',
                        taskDetails: entry.CustomWorkingDescription,
                        pricingType: getPricingTypeName(entry.articleNumber),
                        price: `${entry.amountPerUnit} SEK`,
                        hours: entry.articleNumber === '92' ? formatHours(entry.workingHour) : '01:00',
                        holiday: 'No',
                        articleNumber: entry.articleNumber
                    }))
                };
           }).sort((a, b) => a.weekNumber - b.weekNumber);

            setWeekGroups(newWeekGroups);
        }
    }, [timesheetData]);

    // Update timesheetData when fetched from Redux
    useEffect(() => {
        if (fetchedTimesheetData && fetchedTimesheetData.length > 0) {
            
            // Map API response to TimesheetDataEntry format
            const mappedData: TimesheetDataEntry[] = fetchedTimesheetData.map((item: any) => {
                // Get customer name from customers array by matching customerId
                let customerName = '';
                if (item.customerId && customers && customers.length > 0) {
                    const customer = customers.find((c: any) => {
                        const custId = c.customerId || c.id || c.customer_id;
                        return custId === item.customerId;
                    });
                    customerName = customer ? (customer.name || customer.customerName || customer.customer_name || '') : '';
                }
                
                return {
                    id: item.timeSheetWorkingHourId || item.id || 0,
                    timeSheetWorkingHourId: item.timeSheetWorkingHourId || item.id || 0,
                    workingDate: item.workingDate.split('T')[0].replace(/-/g, '/'), // Convert "2026-01-01T00:00:00" to "2026/01/01"
                    dateId: item.dateId || item.workingDate.split('T')[0].replace(/-/g, ''),
                    number: item.number || 0,
                    unit: item.unit || 'Tim',
                    vat: item.vat || 0,
                    isArticleShownInInvoice: item.isArticleShownInInvoice ?? true,
                    amountPerUnit: item.amountPerUnit || 0,
                    amount: item.amount || 0,
                    articleNumber: item.articleNumber || '92',
                    workingDescription: item.workingDescription || '',
                    houseworkType: item.houseworkType || '',
                    workingHour: item.workingHour || 0,
                    CustomWorkingDescription: item.customWorkingDescription || '',
                    userId: item.userId || 0,
                    customerId: item.customerId,
                    customerName: customerName
                };
            });
            
            setTimesheetData(mappedData);
        } else if (fetchedTimesheetData && fetchedTimesheetData.length === 0) {
            // If API returns empty array, reset to show one empty row
            setTimesheetData([]);
            setWeekGroups([
                {
                    weekNumber: 24,
                    startDate: '',
                    endDate: '',
                    entries: [
                        {
                            id: '1',
                            date: '',
                            taskDetails: '',
                            pricingType: 'Hourly',
                            price: 'Kr',
                            hours: '00:00',
                            holiday: 'No',
                            articleNumber: '92'
                        }
                    ]
                }
            ]);
        }
    }, [fetchedTimesheetData, customers]);

    // Helper function to get week number
    const getWeekNumber = (date: Date): number => {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const dayNum = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    };

    // Helper function to format date
    const formatDate = (date: Date): string => {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}-${day}-${year}`;
    };

    // Helper function to format date for input field (YYYY-MM-DD)
    const formatDateForInput = (dateString: string): string => {
        // dateString is in format "2026/01/01"
        const [year, month, day] = dateString.split('/');
        return `${year}-${month}-${day}`;
    };

    // Helper function to get pricing type name from article number
    const getPricingTypeName = (articleNumber: string): string => {
        const pricingMap: { [key: string]: string } = {
            '92': 'Hourly',
            '94': 'Payment per day',
            '95': 'Payment per week',
            '96': 'Payment per month',
            '97': 'Payment per Agreed'
        };
        return pricingMap[articleNumber] || 'Hourly';
    };

    // Helper function to get pricing type details (unit and articleNumber)
    const getPricingTypeDetails = (pricingType: string): { unit: string; articleNumber: string } => {
        const pricingMap: { [key: string]: { unit: string; articleNumber: string } } = {
            'Hourly': { unit: 'Tim', articleNumber: '92' },
            'Payment per day': { unit: 'Dag', articleNumber: '94' },
            'Payment per week': { unit: 'vecka', articleNumber: '95' },
            'Payment per month': { unit: 'm', articleNumber: '96' },
            'Payment per Agreed': { unit: 'a', articleNumber: '97' }
        };
        return pricingMap[pricingType] || { unit: 'Tim', articleNumber: '92' };
    };

    // Helper function to format hours
    const formatHours = (hours: number): string => {
        const wholeHours = Math.floor(hours);
        const minutes = Math.round((hours - wholeHours) * 60);
        return `${String(wholeHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    };

    const handleInvoiceByTimesheet = () => {
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
    };

    const handleModalSubmit = (data: InvoiceByTimesheetData) => {
        // TODO: Implement invoice generation with data
        setIsModalOpen(false);
    };

    const handleLoad = () => {
        // Reset to page 1 when loading new date range
        setCurrentPage(1);
        
        // Reset selected client to empty/default state
        setSelectedClient('');
        
        // Dispatch API call with date parameters and page 1
        dispatch(
            getTimesheetCreationRecords({
                fromDate: fromDate,
                toDate: toDate,
                customerId: '',
                pageNumber: 1
            })
        );
    };

    const handleSaveTimesheet = () => {
        setIsSaveConfirmationOpen(true);
    };

    const saveTimesheetData = async () => {
        if (timesheetData.length === 0) {
            return;
        }

        // Get customerId from the first entry (all entries should have the same customer)
        const customerId = timesheetData[0]?.customerId || 0;

        // Filter only new entries (id === 0) to save
        const newEntries = timesheetData.filter(entry => entry.id === 0);

        if (newEntries.length === 0) {
            return;
        }

        // Map timesheetData to match API structure
        const mappedData = newEntries.map(entry => ({
            ...entry,
            customWorkingDescription: entry.CustomWorkingDescription
        }));

        await dispatch(
            addTimesheetWorkingHours({
                customerId,
                timeSheetWorkingHoursV2: mappedData
            })
        ).unwrap();
        
        // Show success toast
        toast.success(t('TIMESHEET_SAVED_SUCCESSFULLY') as string);
        
        // Clear timesheet data after successful save
        setTimesheetData([]);
        
        // Reset weekGroups to default empty state
        setWeekGroups([
            {
                weekNumber: 24,
                startDate: '',
                endDate: '',
                entries: [
                    {
                        id: '1',
                        date: '',
                        taskDetails: '',
                        pricingType: 'Hourly',
                        price: 'SEK',
                        hours: '00:00',
                        holiday: 'No',
                        articleNumber: '92'
                    },
                ]
            }
        ]);
        
        // Reset selected client to clear the dropdown
        setSelectedClient('');
        
        // Fetch updated timesheet data after successful save
        dispatch(
            getTimesheetCreationRecords({
                fromDate: '',
                toDate: '',
                customerId: '',
                pageNumber: 1
            })
        );
    };

    const handleDiscardNewEntries = () => {
        // Remove all new entries (id === 0) from timesheetData
        const filteredData = timesheetData.filter(entry => entry.id !== 0);
        
        // Update timesheetData
        setTimesheetData(filteredData);
        
        // If no data remains after filtering, reset to default empty state
        if (filteredData.length === 0) {
            setWeekGroups([
                {
                    weekNumber: 24,
                    startDate: '',
                    endDate: '',
                    entries: [
                        {
                            id: '1',
                            date: '',
                            taskDetails: '',
                            pricingType: 'Hourly',
                            price: 'SEK',
                            hours: '00:00',
                            holiday: 'No',
                            articleNumber: '92'
                        },
                    ]
                }
            ]);
        }
        
        // Reset selected client to clear the dropdown
        setSelectedClient('');
    };

    const handleConfirmSave = async () => {
        try {
            await saveTimesheetData();
            // Close modal on success
            setIsSaveConfirmationOpen(false);
        } catch (error) {
            // Modal stays open on error so user can retry
        }
    };

    const handleCancelSave = () => {
        setIsSaveConfirmationOpen(false);
    };

    const handleEditRow = (entryId: string) => {
        // Store original data before editing
        const originalTimesheetEntry = timesheetData.find(data => `${data.id}-${data.dateId}` === entryId);
        
        // Find the original week entry
        let originalWeekEntry: TimesheetEntry | null = null;
        for (const group of weekGroups) {
            const entry = group.entries.find(e => e.id === entryId);
            if (entry) {
                // Deep clone the entry to store original values
                originalWeekEntry = { ...entry };
                break;
            }
        }
        
        setOriginalEditData({
            timesheetEntry: originalTimesheetEntry ? { ...originalTimesheetEntry } : null,
            weekEntry: originalWeekEntry
        });
        setEditingRowId(entryId);
        setHasUnsavedChanges(false); // Reset changes flag when starting edit
    };

    const handleSaveEdit = () => {
        setIsEditConfirmationOpen(true);
    };

    const handleConfirmEdit = async () => {
        if (!editingRowId) return;

        // Find the edited entry in timesheetData
        const editedEntry = timesheetData.find(data => `${data.id}-${data.dateId}` === editingRowId);
        
        if (!editedEntry) {
            setIsEditConfirmationOpen(false);
            return;
        }

        // Get customerId
        const customerId = editedEntry.customerId || 0;

        // Prepare payload with edited entry
        const mappedData = [{
            id: editedEntry.id,
            timeSheetWorkingHourId: editedEntry.timeSheetWorkingHourId || editedEntry.id,
            invoiceId: 0,
            workingDate: editedEntry.workingDate,
            houseworkType: editedEntry.houseworkType || '',
            workingHour: editedEntry.workingHour,
            unit: editedEntry.unit,
            userId: editedEntry.userId,
            customerId: editedEntry.customerId || 0,
            workingDescription: editedEntry.workingDescription || '',
            customWorkingDescription: editedEntry.CustomWorkingDescription,
            amountPerUnit: editedEntry.amountPerUnit,
            amountPerUnitSek: 0,
            foodAllowance: 0,
            number: editedEntry.number,
            articleNumber: editedEntry.articleNumber,
            dateId: editedEntry.dateId,
            isTaxFreeArticle: true,
            isArticleShownInInvoice: editedEntry.isArticleShownInInvoice,
            isPdfAttachedToInvoice: true,
            tripStartedPlace: '',
            tripEndedPlace: '',
            distance: 0,
            country: '',
            allowancePerDay: 0,
            reasonForTrip: '',
            carRegistrationNo: '',
            vat: editedEntry.vat,
            tripStartedDate: '',
            tripEndedDate: '',
            amount: editedEntry.amount,
            amountInSek: 0,
            totalDayOfTravels: 0,
            allowanceDestination: '',
            description: '',
            expenseCategory: '',
            fileName: '',
            fileContent: ''
        }];

        try {
            await dispatch(
                updateTimesheetHours({
                    customerId,
                    timeSheetWorkingHoursV2: mappedData
                })
            ).unwrap();
            
            // Close modal and reset editing state
            setEditingRowId(null);
            setIsEditConfirmationOpen(false);
            setHasUnsavedChanges(false); // Reset changes flag after save
            
            // Refresh data with current page
            dispatch(
                getTimesheetCreationRecords({
                    fromDate: fromDate,
                    toDate: toDate,
                    customerId: '',
                    pageNumber: currentPage
                })
            );
        } catch (error) {
            // Modal stays open on error so user can retry
        }
    };

    const handleCancelEdit = () => {
        setIsEditConfirmationOpen(false);
    };

    const handleAddNewRow = () => {
        // Get the last week from weekGroups
        const lastWeekGroup = weekGroups[weekGroups.length - 1];
        if (!lastWeekGroup || lastWeekGroup.entries.length === 0) {
            return;
        }
        
        // Get the last entry from the last week
        const lastEntry = lastWeekGroup.entries[lastWeekGroup.entries.length - 1];
        
        // Find the corresponding data in timesheetData using the entry ID
        const lastDataEntry = timesheetData.find(data => `${data.id}-${data.dateId}` === lastEntry.id);
        
        // Calculate next date from last entry's date
        let nextDateFormatted = ''; // YYYY/MM/DD format for timesheetData
        let nextDateId = ''; // YYYYMMDD format for dateId
        
        if (lastEntry?.date) {
            // Use last week group entry's date (format: YYYY-MM-DD)
            const [year, month, day] = lastEntry.date.split('-').map(Number);
            const lastDate = new Date(year, month - 1, day); // month is 0-indexed
            lastDate.setDate(lastDate.getDate() + 1);
            const nextYear = lastDate.getFullYear();
            const nextMonth = String(lastDate.getMonth() + 1).padStart(2, '0');
            const nextDay = String(lastDate.getDate()).padStart(2, '0');
            nextDateFormatted = `${nextYear}/${nextMonth}/${nextDay}`;
            nextDateId = `${nextYear}${nextMonth}${nextDay}`;
        } else {
            // Use today's date if no last entry date
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            nextDateFormatted = `${year}/${month}/${day}`;
            nextDateId = `${year}${month}${day}`;
        }
        
        // Create unique dateId with timestamp to prevent duplicate keys
        const uniqueDateId = `${nextDateId}-${Date.now()}`;

        // Get data to copy from last entry (prioritize timesheetData, fallback to weekGroups entry)
        const customerName = lastDataEntry?.customerName || lastEntry?.customerName || '';
        const taskDetails = lastDataEntry?.CustomWorkingDescription || lastEntry?.taskDetails || '';
        const articleNumber = lastDataEntry?.articleNumber || lastEntry?.articleNumber || '92';
        const pricingType = getPricingTypeName(articleNumber);
        const amountPerUnit = lastDataEntry?.amountPerUnit || parseFloat(lastEntry?.price.replace(/[^0-9.]/g, '') || '0') || 0;
        const hoursValue = lastEntry?.hours || '00:00';
        const [hrs, mins] = hoursValue.split(':').map(v => parseInt(v) || 0);
        const decimalHours = hrs + (mins / 60);
        
        // Get pricing details
        const pricingDetails = getPricingTypeDetails(pricingType);
        const number = pricingDetails.articleNumber === '92' ? decimalHours : 1;

        // Only update timesheetData - let the useEffect regenerate weekGroups
        setTimesheetData(prevData => {
            const newDataEntry: TimesheetDataEntry = {
                id: 0, // New entry, will be assigned by backend
                timeSheetWorkingHourId: undefined, // New entry, no ID yet
                workingDate: nextDateFormatted,
                dateId: uniqueDateId, // Use unique dateId with timestamp to prevent duplicate keys
                number: number,
                unit: pricingDetails.unit,
                vat: lastDataEntry?.vat || 0,
                isArticleShownInInvoice: true,
                amountPerUnit: amountPerUnit,
                amount: number * amountPerUnit,
                articleNumber: articleNumber,
                workingDescription: lastDataEntry?.workingDescription || '',
                houseworkType: lastDataEntry?.houseworkType || '',
                workingHour: decimalHours,
                CustomWorkingDescription: taskDetails,
                userId: lastDataEntry?.userId || 0,
                customerId: lastDataEntry?.customerId || undefined,
                customerName: customerName
            };
            
            return [...prevData, newDataEntry];
        });
    };

    // Pagination - now handled by API
    const totalPages = numberOfPages || 1;

    const paginate = (pageNumber: number) => {
        setCurrentPage(pageNumber);
        
        // Call API with new page number
        dispatch(
            getTimesheetCreationRecords({
                fromDate: fromDate,
                toDate: toDate,
                customerId: '',
                pageNumber: pageNumber
            })
        );
    };

    // Check if there are any new entries (id === 0) to enable save button
    const hasNewEntries = timesheetData.some(entry => entry.id === 0);

    return (
        <div className="timesheet-creation">
            <div className="timesheet-records-card">
                <TimesheetFilterHeader
                    fromDate={fromDate}
                    toDate={toDate}
                    onFromDateChange={setFromDate}
                    onToDateChange={setToDate}
                    onLoad={handleLoad}
                    onSave={handleSaveTimesheet}
                    hasNewEntries={hasNewEntries}
                />
                <TimesheetHeader
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                    selectedClient={selectedClient}
                    onClientChange={setSelectedClient}
                    selectedDate={selectedDate}
                    onDateChange={setSelectedDate}
                    onInvoiceByTimesheet={handleInvoiceByTimesheet}
                    onAddRecord={handleAddRecord}
                    fromDate={fromDate}
                    toDate={toDate}
                    hasNewEntries={hasNewEntries}
                    timesheetData={timesheetData}
                    onSaveTimesheet={saveTimesheetData}
                    onDiscardNewEntries={handleDiscardNewEntries}
                />
                
                <div className="timesheet-records-body">
                    <div className="timesheet-table-wrapper">
                        <div className="timesheet-table">
                            {/* Table Header */}
                            <div className="table-header-row">
                                <div className="table-cell header-cell col-date">{t('DATE')}</div>
                                <div className="table-cell header-cell col-customer">{t('CUSTOMER')}</div>
                                <div className="table-cell header-cell col-task">{t('TASK_DETAILS_HEADER')}</div>
                                <div className="table-cell header-cell col-pricing">{t('PRICING_TYPE')}</div>
                                <div className="table-cell header-cell col-price">{t('PRICE_HEADER')}</div>
                                <div className="table-cell header-cell col-hours">{t('HOURS_HH_MM')}</div>
                                {/* <div className="table-cell header-cell col-holiday">{t('HOLIDAY')}</div> */}
                                <div className="table-cell header-cell col-actions"></div>
                            </div>

                        {/* Table Body */}
                        {weekGroups.map((group, weekIndex) => (
                            <div key={`week-${weekIndex}-${group.weekNumber}-${group.startDate}`} className="week-group">
                                {/* Week Header Row */}
                                <div className="week-header-row">
                                    <div className="week-header-content">
                                        {/* <svg className="calendar-icon-week" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                            <path 
                                                d="M5.33333 1.33334V3.33334M10.6667 1.33334V3.33334M2.33333 6.06001H13.6667M14.3333 5.66668V11.3333C14.3333 13.3333 13.3333 14.6667 11 14.6667H5C2.66667 14.6667 1.66667 13.3333 1.66667 11.3333V5.66668C1.66667 3.66668 2.66667 2.33334 5 2.33334H11C13.3333 2.33334 14.3333 3.66668 14.3333 5.66668Z" 
                                                stroke="#68727D" 
                                                strokeWidth="1.5" 
                                                strokeMiterlimit="10" 
                                                strokeLinecap="round" 
                                                strokeLinejoin="round"
                                            />
                                            <path 
                                                d="M10.4633 9.13333H10.4693M10.4633 11.1333H10.4693M7.99665 9.13333H8.00264M7.99665 11.1333H8.00264M5.52938 9.13333H5.53536M5.52938 11.1333H5.53536" 
                                                stroke="#68727D" 
                                                strokeWidth="2" 
                                                strokeLinecap="round" 
                                                strokeLinejoin="round"
                                            />
                                        </svg> */}
                                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
  <path d="M2.75 9.16668H19.25M7.33333 5.50001V1.83334M14.6667 5.50001V1.83334M8.61667 20.1667H13.3833C15.4369 20.1667 16.4636 20.1667 17.248 19.767C17.9379 19.4155 18.4988 18.8546 18.8504 18.1646C19.25 17.3803 19.25 16.3535 19.25 14.3V9.53334C19.25 7.47982 19.25 6.45305 18.8504 5.66871C18.4988 4.97878 17.9379 4.41786 17.248 4.06632C16.4636 3.66668 15.4369 3.66668 13.3833 3.66668H8.61667C6.56314 3.66668 5.53638 3.66668 4.75203 4.06632C4.06211 4.41786 3.50118 4.97878 3.14964 5.66871C2.75 6.45305 2.75 7.47982 2.75 9.53334V14.3C2.75 16.3535 2.75 17.3803 3.14964 18.1646C3.50118 18.8546 4.06211 19.4155 4.75203 19.767C5.53638 20.1667 6.56314 20.1667 8.61667 20.1667Z" stroke="#252525" stroke-width="2" stroke-linecap="round"/>
</svg>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                            <span className="week-label" style={{color: 'var(--text-icons-light-base-main, #252525)',

/* Body */
fontFamily: "Plus Jakarta Sans",
fontSize: "16px",
fontStyle: "normal",
fontWeight: 400,
lineHeight: "140%", /* 22.4px */}}>
                                                {t('WEEK')} {group.weekNumber} ({group.startDate} - {group.endDate})
                                            </span>
                                            {/* {group.customerName && (
                                                <span className="customer-label">
                                                    {t('CUSTOMER')}: {group.customerName}
                                                </span>
                                            )} */}
                                        </div>
                                    </div>
                                </div>

                                {/* Week Entries */}
                                {group.entries.map((entry) => {
                                    // Check if this is a new entry (id starts with "0-")
                                    const isNewEntry = entry.id.startsWith('0-');
                                    // New entries are always editable, existing entries only when in edit mode
                                    const isEditable = isNewEntry || editingRowId === entry.id;
                                    
                                    // Determine background color: editing (blue), new entry (light green), default (transparent)
                                    const backgroundColor = editingRowId === entry.id 
                                        ? '#f0f7ff' 
                                        : isNewEntry 
                                        ? '#f0fdf4' 
                                        : 'transparent';
                                    
                                    // Check if any required field is empty
                                    const isDateEmpty = !entry.date || entry.date === '';
                                    const isTaskEmpty = !entry.taskDetails || entry.taskDetails.trim() === '';
                                    const priceValue = (entry.price || '').replace(' SEK', '').replace('SEK', '').trim();
                                    const isPriceEmpty = !priceValue || priceValue === '' || priceValue === '0' || parseFloat(priceValue) === 0;
                                    const isHoursEmpty = entry.articleNumber === '92' && (!entry.hours || entry.hours === '00:00' || entry.hours.trim() === '');
                                    const hasAnyEmptyField = isDateEmpty || isTaskEmpty || isPriceEmpty || isHoursEmpty;
                                    
                                    return (
                                    <div 
                                        key={entry.id} 
                                        className="table-data-row"
                                        style={{
                                            backgroundColor: backgroundColor,
                                            transition: 'background-color 0.2s ease'
                                        }}
                                    >
                                        {/* Date Column */}
                        <div className="table-cell data-cell col-date" style={{ position: 'relative', overflow: 'visible', zIndex: editingRowId === entry.id || entry.id.startsWith('0-') ? 100 : 1 }}>
                            <input
                                type="date"
                                className="cell-input date-input"
                                value={entry.date}
                                onChange={(e) => handleEntryChange(weekIndex, entry.id, 'date', e.target.value)}
                                disabled={!isEditable}
                                style={{
                                    border: (isDateEmpty && isEditable) ? '1px solid #DC3545' : '1px solid #EAEBF0',
                                    boxShadow: (isDateEmpty && isEditable) ? '0 0 0 2px rgba(220, 53, 69, 0.1)' : 'none',
                                    position: 'relative',
                                    zIndex: 2}}
                                            />
                                        </div>

                                        {/* Customer Column */}
                                        <div className="table-cell data-cell col-customer">
                                            <input
                                                type="text"
                                                className="cell-input customer-input"
                                                value={entry.customerName || ''}
                                                disabled={true}
                                                readOnly
                                            />
                                        </div>

                                        {/* Task Details Column */}
                                        <div className="table-cell data-cell col-task" style={{ position: 'relative' }}>
                                            {entry.taskDetails && entry.taskDetails.length > 35 ? (
                                                <Tooltip 
                                                    content={entry.taskDetails}
                                                    position="top"
                                                >
                                                    <input
                                                        type="text"
                                                        className="cell-input task-input"
                                                        value={entry.taskDetails}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            if (value.length <= 42) {
                                                                handleEntryChange(weekIndex, entry.id, 'taskDetails', value);
                                                            }
                                                        }}
                                                        placeholder={t('ENTER_TASK_DETAILS_PLACEHOLDER')}
                                                        disabled={!isEditable}
                                                        maxLength={42}
                                                        style={{
                                                            border: (isTaskEmpty && isEditable) ? '1px solid #DC3545' : '1px solid #EAEBF0',
                                                            boxShadow: (isTaskEmpty && isEditable) ? '0 0 0 2px rgba(220, 53, 69, 0.1)' : 'none',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            backgroundColor: !isEditable ? '#f9fafb' : '#ffffff',
                                                            width: '100%'
                                                        }}
                                                    />
                                                </Tooltip>
                                            ) : (
                                                <input
                                                    type="text"
                                                    className="cell-input task-input"
                                                    value={entry.taskDetails}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (value.length <= 42) {
                                                            handleEntryChange(weekIndex, entry.id, 'taskDetails', value);
                                                        }
                                                    }}
                                                    placeholder={t('ENTER_TASK_DETAILS_PLACEHOLDER')}
                                                    disabled={!isEditable}
                                                    maxLength={42}
                                                    style={{
                                                        border: (isTaskEmpty && isEditable) ? '1px solid #DC3545' : '1px solid #EAEBF0',
                                                        boxShadow: (isTaskEmpty && isEditable) ? '0 0 0 2px rgba(220, 53, 69, 0.1)' : 'none',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        backgroundColor: !isEditable ? '#f9fafb' : '#ffffff',
                                                        width: '100%'
                                                    }}
                                                />
                                            )}
                                        </div>

                                        {/* Pricing Type Column */}
                                        <div className="table-cell data-cell col-pricing">
                                            <select
                                                className="cell-select pricing-select"
                                                value={entry.pricingType}
                                                onChange={(e) => handleEntryChange(weekIndex, entry.id, 'pricingType', e.target.value)}
                                                disabled={!isEditable}
                                            >
                                                <option value="Hourly">{t('HOURLY')}</option>
                                                <option value="Payment per day">{t('PAYMENT_PER_DAY')}</option>
                                                <option value="Payment per week">{t('PAYMENT_PER_WEEK')}</option>
                                                <option value="Payment per month">{t('PAYMENT_PER_MONTH')}</option>
                                                <option value="Payment per Agreed">{t('PAYMENT_PER_AGREED')}</option>
                                            </select>
                                        </div>

                                        {/* Price Column */}
                                        <div className="table-cell data-cell col-price">
                                            <div style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: '8px',
                                                width: '100%',
                                                border: (isPriceEmpty && isEditable) ? '1px solid #DC3545' : '1px solid #EAEBF0',
                                                borderRadius: '6px',
                                                padding: '0 12px',
                                                backgroundColor: !isEditable ? '#f9fafb' : '#ffffff',
                                                boxShadow: (isPriceEmpty && isEditable) ? '0 0 0 2px rgba(220, 53, 69, 0.1)' : 'none'
                                            }}>
                                                <input
                                                    type="text"
                                                    className="cell-input price-input"
                                                    value={entry.price.replace(' SEK', '')}
                                                    onChange={(e) => handleEntryChange(weekIndex, entry.id, 'price', e.target.value)}
                                                    placeholder="0"
                                                    inputMode="decimal"
                                                    disabled={!isEditable}
                                                    style={{
                                                        border: 'none',
                                                        padding: '8px 0',
                                                        flex: 1,
                                                        backgroundColor: 'transparent',
                                                        outline: 'none'
                                                    }}
                                                />
                                                <span style={{
                                                    color: '#68727D',
                                                    fontSize: '14px',
                                                    fontWeight: 500,
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    SEK
                                                </span>
                                            </div>
                                        </div>

                                        {/* Hours Column */}
                                        <div className="table-cell data-cell col-hours">
                                            <input
                                                type="text"
                                                className="cell-input hours-input"
                                                value={entry.hours}
                                                onChange={(e) => handleEntryChange(weekIndex, entry.id, 'hours', e.target.value)}
                                                onBlur={(e) => handleHoursBlur(weekIndex, entry.id, e.target.value)}
                                                placeholder="hh:mm"
                                                disabled={!isEditable || entry.articleNumber !== '92'}
                                                inputMode="numeric"
                                                style={{
                                                    border: (isHoursEmpty && isEditable) ? '1px solid #DC3545' : '1px solid #EAEBF0',
                                                    boxShadow: (isHoursEmpty && isEditable) ? '0 0 0 2px rgba(220, 53, 69, 0.1)' : 'none'
                                                }}
                                            />
                                        </div>

                                        {/* Holiday Column */}
                                        {/* <div className="table-cell data-cell col-holiday">
                                            <select
                                                className="cell-select holiday-select"
                                                value={entry.holiday}
                                                onChange={(e) => handleEntryChange(weekIndex, entry.id, 'holiday', e.target.value)}
                                                disabled={!isEditable}
                                            >
                                                <option value="No">{t('NO')}</option>
                                                <option value="Yes">{t('YES')}</option>
                                            </select>
                                        </div> */}

                                        {/* Actions Column */}
                                        <div className="table-cell data-cell col-actions" style={{border:"1px solid #EAEBF0", height:"inherit", display: "flex", gap: "8px", alignItems: "center", justifyContent: "center"}}>
                                            {/* Only show edit/save button for existing entries (not new entries with id starting with 0-) */}
                                            {!isNewEntry && (
                                                editingRowId === entry.id ? (
                                                    <button 
                                                        className="btn-icon-action" 
                                                        onClick={handleSaveEdit}
                                                        title={hasAnyEmptyField ? t('PLEASE_FILL_ALL_REQUIRED_FIELDS') || 'Please fill all required fields' : 'Save'}
                                                        disabled={!hasUnsavedChanges || hasAnyEmptyField}
                                                        style={{ 
                                                            opacity: (hasUnsavedChanges && !hasAnyEmptyField) ? 1 : 0.5, 
                                                            cursor: (hasUnsavedChanges && !hasAnyEmptyField) ? 'pointer' : 'not-allowed' 
                                                        }}
                                                    >
                                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                            <path 
                                                                d="M13.3334 4L6.00002 11.3333L2.66669 8" 
                                                                stroke="#28A745" 
                                                                strokeWidth="2" 
                                                                strokeLinecap="round" 
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    </button>
                                                ) : (
                                                    <button 
                                                        className="btn-icon-action" 
                                                        onClick={() => handleEditRow(entry.id)}
                                                        title={hasAnyEmptyField ? t('PLEASE_FILL_ALL_REQUIRED_FIELDS') || 'Please fill all required fields' : 'Edit'}
                                                        disabled={hasAnyEmptyField}
                                                        style={{ 
                                                            opacity: !hasAnyEmptyField ? 1 : 0.5, 
                                                            cursor: !hasAnyEmptyField ? 'pointer' : 'not-allowed' 
                                                        }}
                                                    >
                                                       <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
  <path d="M2.33763 11.9955L3.28631 12.3117H3.28631L2.33763 11.9955ZM2.95469 10.1443L2.006 9.82805L2.006 9.82805L2.95469 10.1443ZM3.59139 9.11407L4.2985 9.82118L4.2985 9.82118L3.59139 9.11407ZM9.74052 2.96495L9.03341 2.25784L9.03341 2.25784L9.74052 2.96495ZM13.0351 6.25949L13.7422 6.96659H13.7422L13.0351 6.25949ZM6.88593 12.4086L6.17882 11.7015L6.17882 11.7015L6.88593 12.4086ZM5.85572 13.0453L6.17195 13.994H6.17195L5.85572 13.0453ZM4.00455 13.6624L4.32078 14.6111H4.32078L4.00455 13.6624ZM6.5482 12.7238L5.94596 11.9255L5.94596 11.9255L6.5482 12.7238ZM6.2887 12.8842L5.844 11.9885L6.2887 12.8842ZM13.8287 3.8808L12.9327 4.32482V4.32482L13.8287 3.8808ZM13.8287 5.34363L12.9327 4.89961V4.89961L13.8287 5.34363ZM10.6564 2.17128L11.1004 3.0673L11.1004 3.0673L10.6564 2.17128ZM12.1192 2.17128L11.6752 3.0673L11.6752 3.0673L12.1192 2.17128ZM3.11578 9.7113L4.01146 10.156L4.01146 10.156L3.11578 9.7113ZM3.27617 9.4518L2.47785 8.84955L2.47785 8.84956L3.27617 9.4518ZM2.03067 13.4702L1.08876 13.8061L2.03067 13.4702ZM2.52981 13.9693L2.19393 14.9112L2.19393 14.9112L2.52981 13.9693ZM9.62398 3.0815C9.23346 2.69098 8.60029 2.69098 8.20977 3.0815C7.81924 3.47203 7.81924 4.10519 8.20977 4.49572L8.91688 3.78861L9.62398 3.0815ZM11.5043 7.79025C11.8948 8.18078 12.528 8.18078 12.9185 7.79025C13.309 7.39973 13.309 6.76656 12.9185 6.37604L12.2114 7.08315L11.5043 7.79025ZM2.33763 11.9955L3.28631 12.3117L3.90337 10.4605L2.95469 10.1443L2.006 9.82805L1.38895 11.6792L2.33763 11.9955ZM3.59139 9.11407L4.2985 9.82118L10.4476 3.67205L9.74052 2.96495L9.03341 2.25784L2.88429 8.40696L3.59139 9.11407ZM13.0351 6.25949L12.3279 5.55238L6.17882 11.7015L6.88593 12.4086L7.59304 13.1157L13.7422 6.96659L13.0351 6.25949ZM5.85572 13.0453L5.53949 12.0966L3.68832 12.7137L4.00455 13.6624L4.32078 14.6111L6.17195 13.994L5.85572 13.0453ZM6.88593 12.4086L6.17882 11.7015C5.99587 11.8845 5.96924 11.908 5.94596 11.9255L6.5482 12.7238L7.15044 13.5221C7.31038 13.4015 7.44783 13.2609 7.59304 13.1157L6.88593 12.4086ZM5.85572 13.0453L6.17195 13.994C6.36676 13.9291 6.55397 13.869 6.73341 13.7799L6.2887 12.8842L5.844 11.9885C5.81788 12.0015 5.78495 12.0148 5.53949 12.0966L5.85572 13.0453ZM6.5482 12.7238L5.94596 11.9255C5.914 11.9496 5.87986 11.9707 5.844 11.9885L6.2887 12.8842L6.73341 13.7799C6.88009 13.7071 7.0197 13.6208 7.15045 13.5221L6.5482 12.7238ZM13.0351 2.96495L12.3279 3.67205C12.8341 4.17823 12.9039 4.26671 12.9327 4.32482L13.8287 3.8808L14.7247 3.43679C14.5135 3.01045 14.1342 2.64984 13.7422 2.25784L13.0351 2.96495ZM13.0351 6.25949L13.7422 6.96659C14.1342 6.5746 14.5135 6.21398 14.7247 5.78764L13.8287 5.34363L12.9327 4.89961C12.9039 4.95773 12.8341 5.0462 12.3279 5.55238L13.0351 6.25949ZM13.8287 3.8808L12.9327 4.32482C13.0224 4.50591 13.0224 4.71852 12.9327 4.89961L13.8287 5.34363L14.7247 5.78765C15.0918 5.047 15.0918 4.17743 14.7247 3.43679L13.8287 3.8808ZM9.74052 2.96495L10.4476 3.67205C10.9538 3.16588 11.0423 3.0961 11.1004 3.0673L10.6564 2.17128L10.2124 1.27527C9.78602 1.48653 9.4254 1.86585 9.03341 2.25784L9.74052 2.96495ZM13.0351 2.96495L13.7422 2.25784C13.3502 1.86585 12.9896 1.48653 12.5632 1.27527L12.1192 2.17128L11.6752 3.0673C11.7333 3.0961 11.8218 3.16588 12.3279 3.67205L13.0351 2.96495ZM10.6564 2.17128L11.1004 3.0673C11.2815 2.97757 11.4941 2.97757 11.6752 3.0673L12.1192 2.17128L12.5632 1.27527C11.8226 0.908245 10.953 0.908245 10.2124 1.27527L10.6564 2.17128ZM2.95469 10.1443L3.90337 10.4605C3.98519 10.215 3.9985 10.1821 4.01146 10.156L3.11578 9.7113L2.22011 9.26659C2.13102 9.44603 2.07094 9.63324 2.006 9.82805L2.95469 10.1443ZM3.59139 9.11407L2.88429 8.40696C2.73908 8.55217 2.5985 8.68962 2.47785 8.84955L3.27617 9.4518L4.07448 10.054C4.09204 10.0308 4.11555 10.0041 4.2985 9.82118L3.59139 9.11407ZM3.11578 9.7113L4.01146 10.156C4.02927 10.1201 4.05037 10.086 4.07448 10.054L3.27617 9.4518L2.47785 8.84956C2.37922 8.9803 2.29294 9.11991 2.22011 9.26659L3.11578 9.7113ZM2.33763 11.9955L1.38895 11.6792C1.26112 12.0627 1.14235 12.4159 1.07429 12.7055C1.00971 12.9803 0.93961 13.3878 1.08876 13.8061L2.03067 13.4702L2.97257 13.1343C3.0263 13.285 2.97556 13.3575 3.02125 13.163C3.06346 12.9834 3.14588 12.733 3.28631 12.3117L2.33763 11.9955ZM4.00455 13.6624L3.68832 12.7137C3.26703 12.8541 3.0166 12.9365 2.83698 12.9787C2.64253 13.0244 2.71501 12.9737 2.86569 13.0274L2.52981 13.9693L2.19393 14.9112C2.61219 15.0604 3.01967 14.9903 3.29449 14.9257C3.58415 14.8576 3.9373 14.7389 4.32078 14.6111L4.00455 13.6624ZM2.03067 13.4702L1.08876 13.8061C1.27262 14.3217 1.67835 14.7274 2.19393 14.9112L2.52981 13.9693L2.86569 13.0274C2.91555 13.0452 2.95479 13.0844 2.97257 13.1343L2.03067 13.4702ZM8.91688 3.78861L8.20977 4.49572L11.5043 7.79025L12.2114 7.08315L12.9185 6.37604L9.62398 3.0815L8.91688 3.78861Z" fill="#0A77FF"/>
</svg>
                                                    </button>
                                                )
                                            )}
                                            {/* Show Cancel button when editing, Delete button when not editing */}
                                            {editingRowId === entry.id ? (
                                                <button 
                                                    className="btn-icon-action" 
                                                    onClick={() => {
                                                        // Restore original data
                                                        if (originalEditData.timesheetEntry) {
                                                            setTimesheetData(prevData => 
                                                                prevData.map(data => 
                                                                    `${data.id}-${data.dateId}` === editingRowId 
                                                                        ? { ...originalEditData.timesheetEntry! }
                                                                        : data
                                                                )
                                                            );
                                                        }
                                                        
                                                        if (originalEditData.weekEntry) {
                                                            setWeekGroups(prevGroups => 
                                                                prevGroups.map(group => ({
                                                                    ...group,
                                                                    entries: group.entries.map(e => 
                                                                        e.id === editingRowId 
                                                                            ? { ...originalEditData.weekEntry! }
                                                                            : e
                                                                    )
                                                                }))
                                                            );
                                                        }
                                                        
                                                        setEditingRowId(null);
                                                        setOriginalEditData({ timesheetEntry: null, weekEntry: null });
                                                        setHasUnsavedChanges(false); // Reset changes flag on cancel
                                                    }}
                                                    title="Cancel"
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                        <path 
                                                            d="M12 4L4 12M4 4L12 12" 
                                                            stroke="#DC3545" 
                                                            strokeWidth="2" 
                                                            strokeLinecap="round" 
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                </button>
                                            ) : (
                                                <button 
                                                    className="btn-icon-action" 
                                                    onClick={() => handleDeleteEntry(weekIndex, entry.id)}
                                                    title="Delete"
                                                >
                                                    <img src="/assets/svg/trash.svg" alt="Delete" width="16" height="16" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                        ))}
                        </div>
                    </div>

                   {/* Add New Row Button - Show only when there are new entries OR client is selected */}
                    {(hasNewEntries || selectedClient) && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            padding: '16px 20px',
                            backgroundColor: '#ffffff',
                            borderTop: '1px solid #eaebf0'
                        }}>
                            <button
                                onClick={handleAddNewRow}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '8px 13px',
                                    backgroundColor: 'transparent',
                                    color: '#6750b8',
                                    border: '2px solid #6750b8',
                                    borderRadius: '50px',
                                    fontSize: '15px',
                                    fontWeight: 600,
                                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                }}
                                // onMouseOver={(e) => {
                                //     e.currentTarget.style.backgroundColor = '#f5f3ff';
                                //     e.currentTarget.style.borderColor = '#5640a3';
                                //     e.currentTarget.style.color = '#5640a3';
                                // }}
                                // onMouseOut={(e) => {
                                //     e.currentTarget.style.backgroundColor = 'transparent';
                                //     e.currentTarget.style.borderColor = '#6750b8';
                                //     e.currentTarget.style.color = '#6750b8';
                                // }}
                            >
                                {/* <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path 
                                        d="M8 3.33334V12.6667M3.33333 8H12.6667" 
                                        stroke="currentColor" 
                                        strokeWidth="2" 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round"
                                    />
                                </svg> */}
                                {t('ADD_RECORD') || 'Add New Row'}
                            </button>
                        </div>
                    )}
                    {/* Pagination */}
                    <div className="timesheet-pagination">
                        <button 
                            className="pagination-btn"
                            disabled={currentPage === 1 || fetchLoading}
                            onClick={() => paginate(currentPage - 1)}
                        >
                            {t('PREVIOUS')}
                        </button>
                        
                        <div className="pagination-numbers">
                            {(() => {
                                const pages = [];
                                const maxPagesToShow = 6;
                                
                                if (totalPages <= maxPagesToShow) {
                                    // Show all pages if total is less than max
                                    for (let i = 1; i <= totalPages; i++) {
                                        pages.push(
                                            <button
                                                key={i}
                                                className={`pagination-number ${currentPage === i ? 'active' : ''}`}
                                                onClick={() => paginate(i)}
                                                disabled={fetchLoading}
                                            >
                                                {i}
                                            </button>
                                        );
                                    }
                                } else {
                                    // Show first page
                                    pages.push(
                                        <button
                                            key={1}
                                            className={`pagination-number ${currentPage === 1 ? 'active' : ''}`}
                                            onClick={() => paginate(1)}
                                            disabled={fetchLoading}
                                        >
                                            1
                                        </button>
                                    );
                                    
                                    // Show dots if current page is far from start
                                    if (currentPage > 3) {
                                        pages.push(<span key="dots-start" className="pagination-dots">...</span>);
                                    }
                                    
                                    // Show pages around current page
                                    const startPage = Math.max(2, currentPage - 1);
                                    const endPage = Math.min(totalPages - 1, currentPage + 1);
                                    
                                    for (let i = startPage; i <= endPage; i++) {
                                        pages.push(
                                            <button
                                                key={i}
                                                className={`pagination-number ${currentPage === i ? 'active' : ''}`}
                                                onClick={() => paginate(i)}
                                                disabled={fetchLoading}
                                            >
                                                {i}
                                            </button>
                                        );
                                    }
                                    
                                    // Show dots if current page is far from end
                                    if (currentPage < totalPages - 2) {
                                        pages.push(<span key="dots-end" className="pagination-dots">...</span>);
                                    }
                                    
                                    // Show last page
                                    pages.push(
                                        <button
                                            key={totalPages}
                                            className={`pagination-number ${currentPage === totalPages ? 'active' : ''}`}
                                            onClick={() => paginate(totalPages)}
                                            disabled={fetchLoading}
                                        >
                                            {totalPages}
                                        </button>
                                    );
                                }
                                
                                return pages;
                            })()}
                        </div>

                        <button 
                            className="pagination-btn"
                            disabled={currentPage === totalPages || fetchLoading}
                            onClick={() => paginate(currentPage + 1)}
                        >
                            {t('NEXT')}
                        </button>
                    </div>
                </div>
            </div>

            <InvoiceByTimesheetModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                onSubmit={handleModalSubmit}
            />

            {/* Save Confirmation Modal */}
            {isSaveConfirmationOpen && (
                <div className="modal-overlay" onClick={handleCancelSave}>
                    <div className="confirmation-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">{t('CONFIRM_SAVE')}</h3>
                            <button className="modal-close-btn" onClick={handleCancelSave}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M18 6L6 18M6 6L18 18" stroke="#68727D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <p className="confirmation-message">{t('ARE_YOU_SURE_SAVE_TIMESHEET')}</p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={handleCancelSave} disabled={savingTimesheet}>
                                {t('NO')}
                            </button>
                            <button className="btn-primary" onClick={handleConfirmSave} disabled={savingTimesheet}>
                                {savingTimesheet ? t('SAVING') : t('YES')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Confirmation Modal */}
            {isEditConfirmationOpen && (
                <div className="modal-overlay" onClick={handleCancelEdit}>
                    <div className="confirmation-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">{t('CONFIRM_EDIT') || 'Confirm Edit'}</h3>
                            <button className="modal-close-btn" onClick={handleCancelEdit}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M18 6L6 18M6 6L18 18" stroke="#68727D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <p className="confirmation-message">{t('ARE_YOU_SURE_SAVE_CHANGES') || 'Are you sure you want to save these changes?'}</p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={handleCancelEdit}>
                                {t('NO')}
                            </button>
                            <button className="btn-primary" onClick={handleConfirmEdit}>
                                {t('YES')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {isDeleteConfirmationOpen && (
                <div className="modal-overlay" onClick={handleCancelDelete}>
                    <div className="confirmation-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">{t('CONFIRM_DELETE')}</h3>
                            <button className="modal-close-btn" onClick={handleCancelDelete}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M18 6L6 18M6 6L18 18" stroke="#68727D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <p className="confirmation-message">{t('ARE_YOU_SURE_DELETE_ENTRY')}</p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={handleCancelDelete} disabled={savingTimesheet}>
                                {t('NO')}
                            </button>
                            <button className="btn-primary" onClick={handleConfirmDelete} disabled={savingTimesheet}>
                                {savingTimesheet ? t('DELETING') : t('YES')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TimesheetCreation;
